import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { isTrackReference, log } from '@livekit/components-core';
import { Participant, ParticipantKind, RoomEvent, Track } from 'livekit-client';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  useCreateLayoutContext,
  useParticipants,
  usePinnedTracks,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import { ParticipantView } from './ParticipantView';
import { AdminCustomTrackToggle } from './AdminCustomTrackToggle';
import VideoLiveView from './VideoLiveView';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { TriggerEventInput } from '@/features/live/types';
import type { VideoLayoutState, VideoPlayerStateControl } from './VideoLiveView';
import PdfLiveView from './PdfLiveView';
import ParticipantTrackPublicationsListener from './ParticipantTrackPublicationsListener';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RoomFailure } from '@/lib/liveRoomConnection';
import { useWebinarT } from '@/hooks/useWebinarT';
import {
  computeCamLayoutRects,
  fitCamLayout,
  pctRectStyle,
  scaleCamLayoutPx,
} from '@/lib/camLayouts';
import { useCamLayout } from '@/features/live/camLayoutContext';
import { useRoomCamLayout } from '../useCamLayoutTransport';
import { fitBox, useAreaSize } from '@/lib/useFittedBox';
import { placeLocalCamLast } from '@/lib/speakerStage';
import { usePretestDevices } from '@/contexts/PretestDevicesContext';
import { useLocalCameraPreview } from '../useLocalCameraPreview';
import SpeakerPreviewTile from './SpeakerPreviewTile';

/** Largeur des contrôles cam rendus hors cadre (bouton 36px + 8px d'écart à la vignette). */
const CAM_CONTROLS_GUTTER_PX = 44;

function RoomConnectionOverlay({
  failure,
  reconnecting,
  onRetry,
  rounded,
}: {
  failure?: RoomFailure | null;
  /** Reprise du SDK en cours : on l'annonce sans bouton, elle se termine seule ou par un échec. */
  reconnecting?: boolean;
  onRetry?: () => void;
  rounded?: boolean;
}) {
  const { t } = useWebinarT('webinars');
  // Doublon et retrait sont définitifs : pas de reprise automatique, donc pas de roue qui tourne.
  const terminal = failure?.kind === 'duplicate' || failure?.kind === 'removed';
  const title = !failure
    ? reconnecting
      ? t('webinars:live.connectionResuming')
      : t('webinars:live.initializing')
    : failure.kind === 'duplicate'
      ? t('webinars:live.connectionDuplicate')
      : failure.kind === 'removed'
        ? t('webinars:live.connectionRemoved')
        : t('webinars:live.connectionLost');
  return (
    <div
      className={`absolute inset-0 z-50 flex items-center justify-center bg-card-inverse backdrop-blur-sm ${rounded ? 'rounded-lg' : ''}`}
    >
      <div className="flex flex-col items-center gap-3 px-6 text-center">
        {!terminal && <Loader2 className="size-12 animate-spin text-white" />}
        <span className="text-sm text-white/90">{title}</span>
        {failure && (
          <>
            {!terminal && (
              <span className="text-xs text-white/60">{t('webinars:live.connectionRetrying')}</span>
            )}
            {onRetry && failure.kind !== 'removed' && (
              <Button type="button" onClick={onRetry}>
                {t('webinars:live.connectionRetryNow')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function LiveKitRoomContent({
  isAdmin,
  isModerator,
  userId,
  workspaceId,
  webinarId,
  sessionId,
  handleTriggerEvent,
  onVideoLayoutChange,
  videoPlayerStateRef,
  hasActiveTab,
  isSpeaker,
  speakerTracksEnabled,
  cameraEnabled,
  microphoneEnabled,
  isMediaInitializing,
  isRoomReconnecting,
  roomFailure,
  onRetryConnection,
  onLocalCameraEnabledChange,
  onLocalMicrophoneEnabledChange,
}: {
  isAdmin?: boolean;
  isModerator?: boolean;
  userId: string;
  workspaceId?: number;
  webinarId?: number;
  sessionId?: number;
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  onVideoLayoutChange?: (state: VideoLayoutState) => void;
  videoPlayerStateRef?: RefObject<VideoPlayerStateControl | null>;
  hasActiveTab?: boolean;
  isSpeaker?: boolean;
  /** Pour les intervenants (`isSpeaker`), tant que ce n’est pas true, les toggles cam/micro ne publient pas sur la room (setup / attente « ajouter au live »). */
  speakerTracksEnabled?: boolean;
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
  isMediaInitializing?: boolean;
  isRoomReconnecting?: boolean;
  /** Connexion à la room en échec : l'overlay le dit et propose de réessayer au lieu d'attendre en silence. */
  roomFailure?: RoomFailure | null;
  onRetryConnection?: () => void;
  onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
  onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
}) {
  const lastAutoFocusedScreenShareTrack = useRef<TrackReferenceOrPlaceholder | null>(null);
  const lastAutoFocusedSpeakerTrack = useRef<TrackReferenceOrPlaceholder | null>(null);
  const room = useRoomContext();

  /** Intervenant lien public : toggles = intent React seule jusqu’à `speakerTracksEnabled` (bouton Ajouter / acceptation modale). */
  const publishLocalMediaToRoom = useMemo(
    () => !isSpeaker || speakerTracksEnabled === true,
    [isSpeaker, speakerTracksEnabled],
  );

  const adminResourceStates = useAppSelector((state: RootState) => state.live.adminResourceStates);
  const participantTrackPublications = useAppSelector(
    (state: RootState) => state.live.participantTrackPublications,
  );

  const publicResource = useAppSelector((state: RootState) => state.live.publicResource);
  const publicSlide = useAppSelector((state: RootState) => state.live.publicSlide);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    {
      updateOnlyOn: [RoomEvent.ActiveSpeakersChanged],
      onlySubscribed: false,
    },
  );

  const localParticipantIdentity = room.localParticipant.identity;

  /** Pin / focus auto : jamais la caméra locale du modérateur (il n’est pas présentateur). */
  const tracksForPinAndFocus = useMemo(() => {
    if (!isModerator) return tracks;
    return tracks.filter((tr) => tr.participant.identity !== localParticipantIdentity);
  }, [tracks, isModerator, localParticipantIdentity]);

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare)
    .filter((track) => !(isModerator && track.participant.identity === localParticipantIdentity));

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  // const carouselTracks = tracks.filter((track) => !isEqualTrackRef(track, focusTrack));

  useEffect(() => {
    // if (!enableAutoFocus) return;

    const handleActiveSpeakersChanged = (speakers: Participant[]) => {
      if (
        screenShareTracks.some((track) => track.publication.isSubscribed)
        // (layoutContext.pin.state?.pinnedRef as any)
      ) {
        return;
      }

      if (speakers.length > 0) {
        const activeSpeaker = speakers[0];
        const speakerTrack = tracksForPinAndFocus.find(
          (tr) =>
            isTrackReference(tr) &&
            tr.participant.identity === activeSpeaker.identity &&
            tr.publication.source === Track.Source.Camera,
        );

        if (
          speakerTrack &&
          isTrackReference(speakerTrack) &&
          (!lastAutoFocusedSpeakerTrack.current ||
            speakerTrack.participant.identity !==
              lastAutoFocusedSpeakerTrack.current.participant.identity)
        ) {
          log.debug('Auto set active speaker focus:', {
            newSpeakerTrack: speakerTrack,
          });
          layoutContext.pin.dispatch?.({
            msg: 'set_pin',
            trackReference: speakerTrack,
          });
          lastAutoFocusedSpeakerTrack.current = speakerTrack;
        }
      }
    };

    room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);

    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
    };
  }, [room, tracksForPinAndFocus, layoutContext, screenShareTracks]);

  useEffect(() => {
    // if (!enableAutoFocus) return;

    if (!focusTrack && !screenShareTracks.some((track) => track.publication.isSubscribed)) {
      const firstVideoTrack = tracksForPinAndFocus.find(
        (tr) => isTrackReference(tr) && tr.publication.source === Track.Source.Camera,
      );

      if (firstVideoTrack && isTrackReference(firstVideoTrack)) {
        log.debug('Setting default focus track:', { track: firstVideoTrack });
        layoutContext.pin.dispatch?.({
          msg: 'set_pin',
          trackReference: firstVideoTrack,
        });
        lastAutoFocusedSpeakerTrack.current = firstVideoTrack;
      }
    }
  }, [focusTrack, tracksForPinAndFocus, screenShareTracks, layoutContext]);

  useEffect(() => {
    // if (!enableAutoFocus) return;

    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug('Auto set screen share focus:', {
        newScreenShareTrack: screenShareTracks[0],
      });
      layoutContext.pin.dispatch?.({
        msg: 'set_pin',
        trackReference: screenShareTracks[0],
      });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
      lastAutoFocusedSpeakerTrack.current = null;
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug('Auto clearing screen share focus.');
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;
    }

    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracksForPinAndFocus.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({
          msg: 'set_pin',
          trackReference: updatedFocusTrack,
        });
      }
    }
  }, [
    screenShareTracks
      .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
      .join(),
    focusTrack?.publication?.trackSid,
    tracksForPinAndFocus,
  ]);

  const participants = useParticipants();

  const filteredTracks = tracks.filter((track) => {
    const isLocal = track.participant.identity === localParticipantIdentity;
    if (isModerator && isLocal) {
      return false;
    }
    if (isLocal) {
      return isTrackReference(track) || track.source === Track.Source.Camera;
    }
    const participant = participants.find((p) => p.identity === track.participant.identity);
    if (participant?.permissions?.canPublish !== true) {
      return false;
    }
    // Garde-fou : un participant non-humain (agent transcription / egress) rejoint la room
    // avec canPublish mais sans piste caméra. On l'écarte pour éviter une vignette vide.
    if (participant.kind !== ParticipantKind.STANDARD) {
      return false;
    }
    return isTrackReference(track) || track.source === Track.Source.Camera;
  });

  const mainScreenShareTrack = useMemo(() => {
    const t = filteredTracks.find(
      (x) => isTrackReference(x) && x.publication?.source === Track.Source.ScreenShare,
    );
    return t && isTrackReference(t) ? t : null;
  }, [filteredTracks]);
  const stageTracks = filteredTracks
    .filter((track) => track.source !== 'screen_share')
    .filter((track) => {
      const id = track.participant.identity;
      if (id === localParticipantIdentity) {
        return true;
      }
      if (id.startsWith('presenter-') || id.startsWith('speaker-')) {
        return participantTrackPublications[id] === true;
      }
      return true;
    });
  const participantViewTracks = isSpeaker
    ? placeLocalCamLast(
        stageTracks,
        (track) => track.participant.identity === localParticipantIdentity,
      )
    : stageTracks;

  // Mise en page caméras : la préférence vient de l'API (défaut du workspace puis metadata de room),
  // les deux régies suivent donc le même état. La préférence est adaptée au nombre de caméras
  // (réduction de taille ou bascule d'option, préférence restaurée dès que le nombre le permet) et
  // la disposition effective est diffusée pour le compositor.
  const {
    preferred: preferredCamLayout,
    effective: receivedCamLayout,
    setEffective: setEffectiveCamLayout,
    applyFromRoom: applyCamLayoutFromRoom,
    canEdit: isCamLayoutWriter,
  } = useCamLayout();
  useRoomCamLayout(room, applyCamLayoutFromRoom);
  const stageCamCount = participantViewTracks.length;
  // L'intervenant compte sa propre vignette (aperçu hors scène) : il adapte la préférence reçue à
  // ce nombre comme le ferait la régie, et retombe sur la disposition du flux une fois en direct.
  const camLayoutConfig =
    isCamLayoutWriter || isSpeaker
      ? fitCamLayout(preferredCamLayout, stageCamCount)
      : receivedCamLayout;
  useEffect(() => {
    if (isCamLayoutWriter) setEffectiveCamLayout(camLayoutConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- comparé par valeur, pas par référence
  }, [isCamLayoutWriter, camLayoutConfig.option, camLayoutConfig.format, camLayoutConfig.size]);

  const slideIsLive = adminResourceStates.some(
    (state) => state.resourceType === 'WEBINAR_SLIDE' && state.live,
  );

  const videoIsLive = adminResourceStates.some(
    (state) => state.resourceType === 'WEBINAR_RESOURCE' && state.live,
  );

  const canManageLiveLayout = Boolean(isAdmin || isModerator);

  const currentVideoIsLive = canManageLiveLayout
    ? videoIsLive
    : isSpeaker
      ? publicResource !== null
      : false;
  const currentSlideIsLive = canManageLiveLayout
    ? slideIsLive
    : isSpeaker
      ? publicSlide !== null
      : false;

  /** Partage d’écran = remplace seulement le PDF / la vidéo (les cams restent en vignettes). */
  const isScreenShareFullBleed = Boolean(mainScreenShareTrack);

  const liveSlideResource = adminResourceStates.find(
    (state) => state.resourceType === 'WEBINAR_SLIDE' && state.live,
  );
  const activePdfSlideId = isSpeaker ? publicSlide?.id : liveSlideResource?.resourceId;

  const [mountedPdfSlideId, setMountedPdfSlideId] = useState<number | null>(null);
  useEffect(() => {
    if (activePdfSlideId != null) {
      setMountedPdfSlideId(activePdfSlideId);
    }
  }, [activePdfSlideId]);

  /** Clé stable quand la slide est masquée ; change tout de suite si on active un autre PDF. */
  const pdfViewKey = activePdfSlideId ?? mountedPdfSlideId;
  const isPdfLayerVisible = currentSlideIsLive && !isScreenShareFullBleed;

  const showPresenterCameraPip = isScreenShareFullBleed || currentVideoIsLive || currentSlideIsLive;

  // Aperçu WYSIWYG (présentateur, modérateur, intervenant) : même géométrie que le flux spectateur
  // (compositor egress).
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  // Page invité (publique) : pas de workspace en store, le branding vient de la session publique.
  const publicVisualIdentity = useAppSelector(
    (state: RootState) => state.live.publicSession?.visualIdentity,
  );
  const stageBackgroundColor =
    currentWorkspace?.visualIdentity?.backgroundColor ||
    publicVisualIdentity?.backgroundColor ||
    '#000000';

  const hasMainAsset = showPresenterCameraPip;
  const stageRects = computeCamLayoutRects(camLayoutConfig, stageCamCount, hasMainAsset);
  const mainStageBoxStyle = stageRects.content
    ? pctRectStyle(stageRects.content)
    : { left: 0, top: 0, width: '100%', height: '100%' };

  // Cadre 16:9 fidèle au compositor (qui bake du 1920×1080) : on mesure le volet et on cale le plus
  // grand 16:9 qui rentre. `h-full aspect-[16/9]` seul échouait si le volet est plus étroit que 16:9.
  const { ref: stageAreaRef, size: stageArea } = useAreaSize<HTMLDivElement>();
  // Gouttière régie réservée aux contrôles cam, rendus hors cadre : sans elle ils seraient rognés
  // quand le volet est trop étroit pour laisser une marge autour du 16:9.
  const camControlsGutterPx = canManageLiveLayout && !isModerator ? CAM_CONTROLS_GUTTER_PX : 0;
  const stageFrame = fitBox(Math.max(0, stageArea.w - camControlsGutterPx), stageArea.h, 16 / 9);

  const camTrackKey = (track: (typeof participantViewTracks)[number]) =>
    isTrackReference(track)
      ? `${track.participant.identity}-${track.publication.trackSid}`
      : track.participant.identity;

  const renderCamTile = (
    track: (typeof participantViewTracks)[number],
    externalTrackControls?: boolean,
  ) => (
    <ParticipantView
      trackRef={track}
      isAdmin={canManageLiveLayout}
      isSpeaker={isSpeaker}
      isModerator={isModerator}
      userId={userId}
      activeAsset={false}
      externalTrackControls={externalTrackControls}
      workspaceId={workspaceId}
      webinarId={webinarId}
      sessionId={sessionId}
      cameraEnabled={cameraEnabled}
      microphoneEnabled={microphoneEnabled}
      onLocalCameraEnabledChange={onLocalCameraEnabledChange}
      onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
      publishLocalMediaToRoom={publishLocalMediaToRoom}
    />
  );

  const renderTrackToggles = (identity: string, className: string) => (
    <div className={className}>
      {([Track.Source.Microphone, Track.Source.Camera] as const).map((source) => (
        <AdminCustomTrackToggle
          key={`${identity}-${source}`}
          source={source}
          isAdmin={canManageLiveLayout}
          isSpeaker={isSpeaker}
          participantIdentity={identity}
          roomName={room.name}
          workspaceId={workspaceId}
          webinarId={webinarId}
          sessionId={sessionId}
          cameraEnabled={cameraEnabled}
          microphoneEnabled={microphoneEnabled}
          publishLocalMediaToRoom={publishLocalMediaToRoom}
          onLocalCameraEnabledChange={onLocalCameraEnabledChange}
          onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
        />
      ))}
    </div>
  );

  // Les vignettes de la colonne sont trop petites pour porter les contrôles : on les rend à gauche,
  // hors tuile et empilés. La tuile garde sa position, seuls les contrôles débordent vers la gauche.
  const renderCamTileControls = (track: (typeof participantViewTracks)[number]) => {
    if (!canManageLiveLayout || isModerator) return null;
    return renderTrackToggles(
      track.participant.identity,
      'absolute right-full top-1/2 z-20 mr-2 flex -translate-y-1/2 flex-col gap-2',
    );
  };

  // Intervenant : ses vignettes sont trop petites pour les boutons inline (15 % du cadre, ~60 px sur
  // mobile), ses contrôles micro/cam vivent dans le coin du cadre.
  const speakerControls = isSpeaker
    ? renderTrackToggles(localParticipantIdentity, 'absolute top-2 right-2 z-20 flex gap-2')
    : null;

  const speakerOffStage = Boolean(isSpeaker) && !publishLocalMediaToRoom;
  const pretestDevices = usePretestDevices();
  const previewTrack = useLocalCameraPreview(
    speakerOffStage && (cameraEnabled ?? true),
    pretestDevices?.cameraId,
  );

  // Vignettes posées aux rectangles de la disposition (colonne, rangée, coin ou grille). Les
  // contrôles cam débordent hors tuile, à gauche, comme avant.
  const renderStageCameras = (frameHeightPx: number | undefined, withControls: boolean) =>
    stageRects.cams.map((rect, index) => {
      const track = participantViewTracks[index];
      if (!track) return null;
      return (
        <div
          key={`${camTrackKey(track)}-${index}`}
          className="absolute z-10"
          style={pctRectStyle(rect)}
        >
          {withControls ? renderCamTileControls(track) : null}
          <div
            className="h-full w-full overflow-hidden [&_video]:scale-[1.005]"
            style={{
              backgroundColor: stageBackgroundColor,
              borderRadius: scaleCamLayoutPx(stageRects.camRadiusPx, frameHeightPx),
            }}
          >
            {speakerOffStage && track.participant.identity === localParticipantIdentity ? (
              <SpeakerPreviewTile track={previewTrack} participant={track.participant} />
            ) : (
              renderCamTile(track, withControls || Boolean(isSpeaker))
            )}
          </div>
        </div>
      );
    });
  const mainStageRadius = scaleCamLayoutPx(stageRects.contentRadiusPx, stageFrame?.height);

  return (
    <div className="h-full w-full min-w-0 flex flex-col mx-auto relative overflow-hidden">
      <ParticipantTrackPublicationsListener />
      {isMediaInitializing && (
        <RoomConnectionOverlay
          failure={roomFailure}
          reconnecting={isRoomReconnecting}
          onRetry={onRetryConnection}
        />
      )}
      {speakerControls}

      {/* PDF : page en haut (cam à gauche), barre de slides pleine largeur en bas (hors cadre). */}
      {pdfViewKey != null && (
        <div
          className={
            isPdfLayerVisible
              ? 'relative flex min-h-0 min-w-0 flex-1 flex-col'
              : 'pointer-events-none invisible absolute inset-0 z-0 flex min-h-0 min-w-0 flex-col'
          }
          aria-hidden={!isPdfLayerVisible}
        >
          <PdfLiveView
            key={pdfViewKey}
            isVisible={isPdfLayerVisible}
            fillStage
            stageRects={isPdfLayerVisible ? stageRects : undefined}
            renderStageCameras={isPdfLayerVisible ? (h) => renderStageCameras(h, true) : undefined}
            handleTriggerEvent={canManageLiveLayout ? handleTriggerEvent : undefined}
            workspaceId={workspaceId}
            webinarId={webinarId}
            sessionId={sessionId}
            hasActiveTab={hasActiveTab}
            isSpeaker={isSpeaker}
          />
        </div>
      )}

      {/* Vidéo / partage / grille : cadre 16:9 letterboxé (pas de barre → pas de conflit). */}
      {!isPdfLayerVisible && (
        <div
          ref={stageAreaRef}
          className="relative flex-1 min-h-0 flex items-center justify-center"
          style={{ paddingLeft: camControlsGutterPx }}
        >
          {/* Boîte de repère non rognante : la colonne caméras en sort pour porter ses contrôles
                dans la marge, alors que le contenu de scène reste rogné (overscan anti-liseré). */}
          <div
            className={`relative ${stageFrame ? '' : 'h-full aspect-[16/9] max-w-full'}`}
            style={stageFrame ? { width: stageFrame.width, height: stageFrame.height } : undefined}
          >
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ backgroundColor: stageBackgroundColor }}
            >
              {!isScreenShareFullBleed && currentVideoIsLive ? (
                <div
                  className="absolute flex items-center justify-center overflow-hidden"
                  style={{ ...mainStageBoxStyle, borderRadius: mainStageRadius }}
                >
                  <VideoLiveView
                    isSpeaker={isSpeaker}
                    isAdmin={canManageLiveLayout}
                    fillStage
                    handleTriggerEvent={canManageLiveLayout ? handleTriggerEvent : undefined}
                    onVideoLayoutChange={canManageLiveLayout ? onVideoLayoutChange : undefined}
                    videoPlayerStateRef={canManageLiveLayout ? videoPlayerStateRef : undefined}
                  />
                </div>
              ) : null}

              {isScreenShareFullBleed &&
              mainScreenShareTrack &&
              isTrackReference(mainScreenShareTrack) ? (
                <div
                  className="absolute flex items-center justify-center overflow-hidden"
                  style={{ ...mainStageBoxStyle, borderRadius: mainStageRadius }}
                >
                  <ParticipantView
                    key={`ss-content-${mainScreenShareTrack.publication.trackSid}`}
                    trackRef={mainScreenShareTrack}
                    isAdmin={canManageLiveLayout}
                    isSpeaker={isSpeaker}
                    isModerator={isModerator}
                    userId={userId}
                    activeAsset={false}
                    // Micro/caméra du présentateur : ils vivent sur sa vignette, pas sur l'écran partagé.
                    externalTrackControls
                    workspaceId={workspaceId}
                    webinarId={webinarId}
                    sessionId={sessionId}
                    cameraEnabled={cameraEnabled}
                    microphoneEnabled={microphoneEnabled}
                    onLocalCameraEnabledChange={onLocalCameraEnabledChange}
                    onLocalMicrophoneEnabledChange={onLocalMicrophoneEnabledChange}
                    publishLocalMediaToRoom={publishLocalMediaToRoom}
                  />
                </div>
              ) : null}

              {!hasMainAsset && stageCamCount > 0
                ? renderStageCameras(stageFrame?.height, false)
                : null}
            </div>

            {hasMainAsset ? renderStageCameras(stageFrame?.height, true) : null}
          </div>
        </div>
      )}
    </div>
  );
}
